<template>
  <div class="cal" :style="accentVars">
    <div class="cal__col">
      <div class="cal__bar">
        <div class="cal__nav">
          <button
            type="button"
            class="cal__today"
            :aria-label="$t('calendar.goToday')"
            @click="setToday"
          >
            {{ $t("calendar.today") }}
          </button>
          <button
            type="button"
            class="cal__step"
            :aria-label="$t('calendar.previous')"
            @click="prev"
          >
            <v-icon size="20" color="var(--dk-ink, #231A2E)"
              >mdi-chevron-left</v-icon
            >
          </button>
          <button
            type="button"
            class="cal__step"
            :aria-label="$t('calendar.next')"
            @click="next"
          >
            <v-icon size="20" color="var(--dk-ink, #231A2E)"
              >mdi-chevron-right</v-icon
            >
          </button>
          <div v-if="$refs.calendar" class="cal__title cal-display">
            {{ $refs.calendar.title }}
          </div>
        </div>

        <div class="cal__views" role="tablist">
          <button
            v-for="view in viewSegments"
            :key="view.value"
            type="button"
            class="cal__view"
            :class="{ 'cal__view--on': type === view.value }"
            @click="type = view.value"
          >
            {{ view.text }}
          </button>
        </div>
      </div>

      <p v-if="dragEnabled" class="cal__hint">
        {{ $t("calendar.moveHint") }}
      </p>

      <div class="cal__grid" :class="{ 'cal__grid--dragging': dragging }">
        <v-calendar
          :locale="$i18n.locale === 'en' ? 'en-GB' : 'pt-BR'"
          ref="calendar"
          v-model="focus"
          :color="accent"
          :events="events"
          :event-color="getEventColor"
          :type="type"
          @click:event="showEvent"
          @click:more="viewDay"
          @click:date="viewDay"
          @mousedown:event="startDrag"
          @mousedown:time="startTime"
          @mousemove:time="mouseMove"
          @mouseup:time="endDrag"
          @mouseleave.native="cancelDrag"
        >
          <template #event="{ event }">
            <div
              class="cal-evt"
              :class="{
                'cal-evt--tentative': isTentative(event),
                'cal-evt--movable': dragEnabled && isMovable(event),
                'cal-evt--dragging': dragging && dragEvent === event,
              }"
              :style="
                isTentative(event)
                  ? { '--evt-color': normalizeColor(event.color) }
                  : null
              "
            >
              {{ event.name }}
            </div>
          </template>
          <!-- Retorno de 30/10 (card 17trt6jfaqt): ao pegar uma aula, os
               horários de disponibilidade do perfil ficam em verde claro. -->
          <template #day-body="{ weekday, timeToY, minutesToPixels }">
            <template v-if="showAvailability">
              <div
                v-for="block in availabilityWindows(weekday)"
                :key="block.from"
                class="cal-avail"
                :style="availabilityStyle(block, timeToY, minutesToPixels)"
                aria-hidden="true"
              ></div>
            </template>
          </template>
        </v-calendar>
        <v-dialog
          :value="!!moveRequest"
          max-width="380"
          persistent
          @keydown.esc="cancelMove"
        >
          <v-card v-if="moveRequest" class="mv" :style="accentVars">
            <h2 class="mv__title">{{ $t("calendar.moveTitle") }}</h2>
            <p class="mv__name">{{ moveRequest.event.name }}</p>
            <dl class="mv__when">
              <dt>{{ $t("calendar.moveFrom") }}</dt>
              <dd class="mv__old">{{ moveRequest.fromLabel }}</dd>
              <dt>{{ $t("calendar.moveTo") }}</dt>
              <dd class="mv__new">{{ moveRequest.toLabel }}</dd>
            </dl>
            <p class="mv__note">{{ $t("calendar.moveNote") }}</p>
            <div class="mv__actions">
              <button
                type="button"
                class="mv__btn mv__btn--ghost"
                :disabled="moveRequest.saving"
                @click="cancelMove"
              >
                {{ $t("calendar.moveCancel") }}
              </button>
              <button
                type="button"
                class="mv__btn mv__btn--solid"
                :disabled="moveRequest.saving"
                @click="confirmMove"
              >
                {{ $t("calendar.moveConfirm") }}
              </button>
            </div>
          </v-card>
        </v-dialog>
        <v-menu
          v-model="selectedOpen"
          :close-on-content-click="false"
          :activator="selectedElement"
          offset-x
        >
          <v-card
            min-width="360"
            max-width="420"
            flat
            class="tkt"
            :style="accentVars"
          >
            <div
              class="tkt__accent"
              :style="{ background: eventAccent(selectedEvent.color) }"
            ></div>

            <div class="tkt__head">
              <div class="tkt__head-main">
                <div class="tkt__name">
                  {{ selectedEvent.name }}
                  <v-icon
                    v-if="selectedEvent.confirmed"
                    size="18"
                    color="#00A379"
                    >mdi-check-decagram</v-icon
                  >
                </div>
                <div v-if="eventTimeRange" class="tkt__time">
                  {{ eventTimeRange }}
                </div>
              </div>

              <!-- Conversação, monitoria e eventos só o administrativo altera
                   (retorno de 22/10, card 17trt6jcypk): sem o menu de opções
                   da aula para o professor, como já era para o aluno. -->
              <v-menu
                offset-y
                v-if="
                  module === 'professor/curso' && !selectedEvent.isGeneralClass
                "
                :disabled="selectedEvent.color === 'red'"
              >
                <template v-slot:activator="{ on, attrs }">
                  <button
                    type="button"
                    class="tkt__more"
                    :aria-label="$t('calendar.lessonOptions')"
                    v-on="on"
                    v-bind="attrs"
                  >
                    <v-icon size="20" color="var(--dk-ink, #231A2E)"
                      >mdi-dots-vertical</v-icon
                    >
                  </button>
                </template>
                <v-list dense>
                  <v-list-item @click="setupAulas(selectedEvent)">
                    <v-list-item-title>{{
                      $t("calendar.lessonOptions")
                    }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <v-menu
                offset-y
                v-if="module === 'aluno/curso' && !selectedEvent.isGeneralClass"
                :disabled="selectedEvent.color === 'red'"
              >
                <template v-slot:activator="{ on, attrs }">
                  <button
                    type="button"
                    class="tkt__more"
                    :aria-label="$t('calendar.requestChange')"
                    v-on="on"
                    v-bind="attrs"
                  >
                    <v-icon size="20" color="var(--dk-ink, #231A2E)"
                      >mdi-dots-vertical</v-icon
                    >
                  </button>
                </template>
                <v-list dense>
                  <v-list-item @click="modalAdiarAluno(selectedEvent)">
                    <v-list-item-title>{{
                      $t("calendar.requestChange")
                    }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>

            <div class="tkt__body">
              <p v-if="selectedEvent.description" class="tkt__desc">
                {{ selectedEvent.description }}
              </p>

              <div v-if="selectedEvent.isGeneralClass" class="tkt__facts">
                <div class="tkt__fact">
                  <span class="tkt__fact-k">{{ $t("calendar.type") }}</span>
                  <span class="tkt__fact-v">{{
                    selectedEvent.generalClassTypeLabel
                  }}</span>
                </div>
                <div class="tkt__fact">
                  <span class="tkt__fact-k">{{ $t("calendar.audience") }}</span>
                  <span class="tkt__fact-v">{{
                    selectedEvent.audienceLabel
                  }}</span>
                </div>
                <!-- Evento pode não ter professor responsável. -->
                <div v-if="selectedEvent.teacherName" class="tkt__fact">
                  <span class="tkt__fact-k">{{ $t("calendar.teacher") }}</span>
                  <span class="tkt__fact-v">{{
                    selectedEvent.teacherName
                  }}</span>
                </div>
                <div class="tkt__fact">
                  <span class="tkt__fact-k">{{ $t("calendar.duration") }}</span>
                  <span class="tkt__fact-v">{{
                    formatDuration(selectedEvent.durationInMinutes)
                  }}</span>
                </div>
                <div class="tkt__fact">
                  <span class="tkt__fact-k">{{ $t("calendar.start") }}</span>
                  <span class="tkt__fact-v">{{
                    formatDateTime(selectedEvent.start)
                  }}</span>
                </div>
                <!-- Chamada feita pelo professor. Só aparece na visão do aluno:
                     o professor conduz a aula e não consta na chamada dela. -->
                <div v-if="selectedEvent.attendanceLabel" class="tkt__fact">
                  <span class="tkt__fact-k">{{
                    $t("calendar.yourAttendance")
                  }}</span>
                  <span
                    class="tkt__fact-v tkt__att"
                    :class="attendanceClass(selectedEvent)"
                  >
                    {{ selectedEvent.attendanceLabel }}
                  </span>
                </div>
              </div>

              <p
                v-if="
                  selectedEvent.isGeneralClass && selectedEvent.addedByTeacher
                "
                class="tkt__note"
              >
                {{ $t("calendar.addedByTeacher") }}
              </p>

              <p
                v-if="
                  selectedEvent.isGeneralClass &&
                  selectedEvent.generalClassType === 'mentoring' &&
                  !selectedEvent.hasMaterial
                "
                class="tkt__note"
              >
                {{ $t("calendar.noMentoringMaterial") }}
              </p>

              <div v-if="!selectedEvent.isGeneralClass" class="tkt__facts">
                <div class="tkt__fact">
                  <span class="tkt__fact-k">{{
                    $t("calendar.classCode")
                  }}</span>
                  <span class="tkt__fact-v">{{ selectedEvent.code }}</span>
                </div>
                <div class="tkt__fact">
                  <span class="tkt__fact-k">{{ $t("calendar.level") }}</span>
                  <span class="tkt__fact-v">{{
                    showLevel(selectedEvent.level)
                  }}</span>
                </div>
              </div>

              <div
                v-if="
                  !selectedEvent.isGeneralClass &&
                  selectedEvent.students &&
                  selectedEvent.students.length
                "
                class="tkt__students"
              >
                <div class="tkt__fact-k">{{ $t("calendar.participants") }}</div>
                <div class="tkt__chips">
                  <span
                    v-for="(student, index) in selectedEvent.students"
                    :key="index"
                    class="tkt__chip"
                    >{{ student.full_name }}</span
                  >
                </div>
              </div>
            </div>

            <div class="tkt__actions">
              <button
                type="button"
                class="tkt__btn tkt__btn--ghost"
                @click="selectedOpen = false"
              >
                {{ $t("common.close") }}
              </button>
              <span class="tkt__spacer"></span>

              <button
                v-if="selectedEvent.hasMaterial"
                type="button"
                class="tkt__btn tkt__btn--ghost"
                @click="abrirMaterial(selectedEvent)"
              >
                {{ $t("calendar.material") }}
              </button>

              <button
                v-if="selectedEvent.link"
                type="button"
                class="tkt__btn tkt__btn--solid"
                @click="joinLesson(selectedEvent)"
              >
                {{ $t("calendar.join") }}
              </button>

              <button
                v-if="
                  module === 'aluno/curso' &&
                  selectedEvent.isGeneralClass &&
                  !selectedEvent.confirmed &&
                  !selectedEvent.attendance &&
                  !hasStarted(selectedEvent)
                "
                type="button"
                class="tkt__btn tkt__btn--solid"
                @click="confirmGeneralClassParticipation(selectedEvent)"
              >
                {{ $t("calendar.willAttend") }}
              </button>

              <span
                v-if="
                  module === 'aluno/curso' &&
                  selectedEvent.isGeneralClass &&
                  selectedEvent.confirmed
                "
                class="tkt__confirmed"
              >
                <v-icon size="16" color="#00A379">mdi-check</v-icon>
                {{ $t("calendar.confirmed") }}
              </span>

              <button
                v-if="
                  module === 'aluno/curso' &&
                  selectedEvent.isGeneralClass &&
                  !selectedEvent.attendance
                "
                type="button"
                class="tkt__btn tkt__btn--danger"
                @click="declineGeneralClassParticipation(selectedEvent)"
              >
                {{ $t("calendar.decline") }}
              </button>

              <!-- Aula de conversação não tem turma: a chamada é feita aqui,
                   pelo professor que a conduz. No evento de professores ele é
                   participante, e a chamada é do administrativo. -->
              <button
                v-if="
                  module === 'professor/curso' &&
                  selectedEvent.isGeneralClass &&
                  selectedEvent.canRegisterAttendance
                "
                type="button"
                class="tkt__btn tkt__btn--solid"
                @click="registerGeneralClassAttendance(selectedEvent)"
              >
                {{ $t("calendar.registerAttendance") }}
              </button>

              <button
                v-if="
                  module === 'professor/curso' && !selectedEvent.isGeneralClass
                "
                type="button"
                class="tkt__btn tkt__btn--ghost"
                @click="setupAulas(selectedEvent)"
              >
                {{ $t("calendar.lessonOptions") }}
              </button>
            </div>
          </v-card>
        </v-menu>
      </div>
    </div>
    <classEditDialog
      ref="dialog"
      :item="focused"
      @confirm="confirmClass"
      @adiar="modalAdiar"
    />
    <adiarAulaDialog ref="adiar" :item="adiando" @confirm="adiarAula" />
    <adiarAulaAlunoDialog
      ref="adiarAluno"
      :item="adiando"
      @confirm="adiarAulaAluno"
    />
  </div>
</template>
<script>
import partnerApi from "@/shared/pitchPartnerApi";
import customerApi from "@/shared/pitchCustomerApi";
import classEditDialog from "./classEditDialog.vue";
import adiarAulaDialog from "./adiarAulaDialog.vue";
import adiarAulaAlunoDialog from "./adiarAulaAlunoDialog.vue";
import { languageTag } from "@/plugins/i18n";
export default {
  name: "AppCalendar",
  components: { classEditDialog, adiarAulaDialog, adiarAulaAlunoDialog },
  props: {
    items: Array,
    // Janelas semanais de disponibilidade do professor: { weekDay, from, to }.
    availability: { type: Array, default: () => [] },
  },
  created() {
    if (this.$route.path.includes("professor")) {
      this.module = "professor/curso";
    } else this.module = "aluno/curso";
  },
  watch: {
    items: {
      immediate: true,
      handler(items = []) {
        this.events = items.map((item) => this.mapItemToEvent(item));
      },
    },
  },
  computed: {
    accent() {
      return this.module === "professor/curso" ? "#A826E1" : "#E8276F";
    },
    accentVars() {
      return {
        "--cal-accent": this.accent,
        "--cal-accent-soft":
          this.module === "professor/curso"
            ? "rgba(168, 38, 225, 0.1)"
            : "rgba(232, 39, 111, 0.1)",
      };
    },
    // Mudar o horario arrastando: so o professor e so onde ha grade de horas.
    dragEnabled() {
      return (
        this.module === "professor/curso" &&
        ["week", "4day", "day"].includes(this.type)
      );
    },
    // Só enquanto uma aula está nas mãos do professor.
    showAvailability() {
      return this.dragEnabled && !!this.dragEvent;
    },
    // Janelas por dia da semana (0 = domingo, como o timestamp do calendário e
    // o getDay da API). Janelas coladas viram uma só, como na regra da API
    // (rearrange.service isInsideTeacherAvailability).
    availabilityByWeekDay() {
      const toMinutes = (clock) => {
        const [hours, minutes] = String(clock || "")
          .split(":")
          .map(Number);
        return hours * 60 + (minutes || 0);
      };

      const byDay = {};
      (this.availability || []).forEach((slot) => {
        const from = toMinutes(slot.from);
        const to = toMinutes(slot.to);
        if (Number.isNaN(from) || Number.isNaN(to) || to <= from) return;
        const day = Number(slot.weekDay);
        (byDay[day] = byDay[day] || []).push({ from, to });
      });

      const pad = (value) => String(value).padStart(2, "0");
      const clock = (total) =>
        pad(Math.floor(total / 60)) + ":" + pad(total % 60);

      Object.keys(byDay).forEach((day) => {
        const sorted = byDay[day].sort((a, b) => a.from - b.from);
        const merged = [];
        sorted.forEach((slot) => {
          const last = merged[merged.length - 1];
          if (last && slot.from <= last.to)
            last.to = Math.max(last.to, slot.to);
          else merged.push({ ...slot });
        });
        byDay[day] = merged.map((slot) => ({
          from: clock(slot.from),
          minutes: slot.to - slot.from,
        }));
      });
      return byDay;
    },
    eventTimeRange() {
      const start = this.selectedEvent && this.selectedEvent.start;
      if (!start) return "";

      const end = this.selectedEvent.end;
      const startLabel = this.formatHour(start);
      const endLabel = end ? this.formatHour(end) : "";

      return endLabel ? `${startLabel}–${endLabel}` : startLabel;
    },
    viewSegments() {
      return [
        { text: this.$t("calendar.month"), value: "month" },
        { text: this.$t("calendar.week"), value: "week" },
        { text: this.$t("calendar.fourDays"), value: "4day" },
        { text: this.$t("calendar.day"), value: "day" },
      ];
    },
  },
  data: () => ({
    focused: {},
    adiando: {},
    focus: "",
    type: "month",
    typeToLabel: {
      month: "Mês",
      week: "Semana",
      day: "Dia",
      "4day": "4 Dias",
    },

    module: null,
    // Arrastar a aula (so o professor, nas visoes com grade de horas).
    dragEvent: null,
    dragTime: null,
    dragOrigin: null,
    dragMoved: false,
    dragging: false,
    lastDragEnd: 0,
    moveRequest: null,
    selectedEvent: {},
    selectedElement: null,
    selectedOpen: false,
    events: [], // Inicialmente vazio,
    englishLevels: [
      {
        text: "Beginner 1",
        value: 0,
      },
      {
        text: "Beginner 2",
        value: 1,
      },
      {
        text: "Essential 1",
        value: 2,
      },
      {
        text: "Essential 2",
        value: 3,
      },
      {
        text: "Pre Intermediate 1",
        value: 4,
      },
      {
        text: "Pre Intermediate 2",
        value: 5,
      },
      {
        text: "Intermediate 1",
        value: 6,
      },
      {
        text: "Intermediate 2",
        value: 7,
      },
      {
        text: "Upper 1",
        value: 8,
      },
      {
        text: "Upper 2",
        value: 9,
      },
      {
        text: "Advanced 1",
        value: 10,
      },
      {
        text: "Advanced 2",
        value: 11,
      },
    ],
  }),
  methods: {
    mapItemToEvent(item) {
      return {
        id: item.id,
        name: `${item.name}`,
        description: item.description,
        start: this.parseDate(item.start),
        end: this.parseDate(item.end),
        color: item.color,
        timed: true,
        link: item.link,
        confirmed: !!item.participationConfirmed,
        // Presença registrada pelo professor na aula de conversação/monitoria.
        // Só vem preenchida na agenda do aluno; o professor é o anfitrião e não
        // consta na própria chamada.
        attendance: item.attendance ?? null,
        attendanceLabel: item.attendanceLabel || null,
        addedByTeacher: !!item.addedByTeacher,
        isGeneralClass: !!item.isGeneralClass,
        // Card 17trt6jcypk: só o anfitrião faz a chamada pela agenda. Sem o
        // campo (API anterior) vale o comportamento antigo.
        canRegisterAttendance: item.canRegisterAttendance !== false,
        generalClassId: item.generalClassId || null,
        generalClassType: item.generalClassType || null,
        generalClassTypeLabel: item.generalClassTypeLabel || null,
        audienceLabel: item.audienceLabel || null,
        teacherName: item.teacherName || null,
        durationInMinutes:
          item.durationInMinutes ||
          this.calculateDurationInMinutes(item.start, item.end),
        level: item.level,
        code: item.code,
        students: item.students || [],
        classId: item.classId,
        // Card 86aje36nz: aula experimental tem menos opções no diálogo.
        isExperimental: !!item.isExperimental,
        content: item.content,
        materialLink: item.materialLink || null,
        hasMaterial: item.isGeneralClass
          ? !!(item.content || item.materialLink)
          : !!item.content,
      };
    },
    parseDate(value) {
      if (!value) return null;

      if (value instanceof Date) return value;

      return new Date(String(value).replace(" ", "T"));
    },
    calculateDurationInMinutes(start, end) {
      const parsedStart = this.parseDate(start);
      const parsedEnd = this.parseDate(end);

      if (!parsedStart || !parsedEnd) return null;

      return Math.max(0, Math.round((parsedEnd - parsedStart) / 60000));
    },
    viewDay({ date }) {
      this.focus = date;
      this.type = "day";
    },
    // Aula geral (conversacao/monitoria) ainda nao confirmada aparece "tentativa"
    // na agenda: sem preenchimento e com borda tracejada, para o aluno aceitar
    // ou recusar ali mesmo (card 86ahk9y7u).
    isTentative(event) {
      // Quem já teve a presença registrada pelo professor não volta a aparecer
      // como "tentativa": a aula aconteceu, não há mais o que aceitar.
      if (event.attendance) return false;
      return !!event.isGeneralClass && !event.confirmed;
    },
    attendanceClass(event) {
      // 1 = PRESENT, 2 = ABSENT (AttendanceStatus da API).
      if (+event.attendance === 1) return "tkt__att--present";
      if (+event.attendance === 2) return "tkt__att--absent";
      return "";
    },
    getEventColor(event) {
      if (this.isTentative(event)) return "transparent";
      return this.normalizeColor(event.color);
    },
    normalizeColor(color) {
      const map = {
        red: "#D52323",
        error: "#D52323",
        green: "#00A379",
        success: "#00A379",
        teal: "#00A379",
        orange: "#FF5D2A",
        amber: "#FF5D2A",
        warning: "#FF5D2A",
        primary: this.accent,
        secondary: this.accent,
        blue: "#3B82F6",
        info: "#3B82F6",
      };

      if (!color) return this.accent;

      return map[String(color).toLowerCase()] || color;
    },
    eventAccent(color) {
      return this.normalizeColor(color);
    },
    formatHour(value) {
      const date = value instanceof Date ? value : this.parseDate(value);

      if (!date || Number.isNaN(date.getTime())) return "";

      return new Intl.DateTimeFormat(languageTag(), {
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    },
    setToday() {
      this.focus = "";
    },
    prev() {
      this.$refs.calendar.prev();
    },
    next() {
      this.$refs.calendar.next();
    },
    availabilityWindows(weekday) {
      return this.availabilityByWeekDay[weekday] || [];
    },
    availabilityStyle(block, timeToY, minutesToPixels) {
      const top = timeToY(block.from);
      if (top === false) return { display: "none" };
      return {
        top: top + "px",
        height: minutesToPixels(block.minutes) + "px",
      };
    },
    // Aula de turma, ainda por acontecer e nao cancelada. Conversacao,
    // monitoria e eventos so o administrativo altera.
    isMovable(event) {
      const start = event && event.start;
      return (
        !!event &&
        !event.isGeneralClass &&
        !!event.classId &&
        String(event.color).toLowerCase() !== "red" &&
        start instanceof Date &&
        start.getTime() > Date.now()
      );
    },
    toTime(tms) {
      return new Date(
        tms.year,
        tms.month - 1,
        tms.day,
        tms.hour,
        tms.minute
      ).getTime();
    },
    // Encaixa de 15 em 15 minutos.
    roundTime(time) {
      const step = 15 * 60 * 1000;
      return time - (time % step);
    },
    startDrag({ event, timed }) {
      if (!this.dragEnabled || !timed || this.moveRequest) return;
      if (!this.isMovable(event)) return;
      this.dragEvent = event;
      this.dragTime = null;
      this.dragMoved = false;
      this.dragOrigin = {
        start: new Date(event.start),
        end: new Date(event.end),
      };
    },
    // O ponto onde o professor pegou a aula: o resto do arrasto parte dele.
    startTime(tms) {
      if (this.dragEvent && this.dragTime === null) {
        this.dragTime = this.toTime(tms) - this.dragEvent.start.getTime();
      }
    },
    mouseMove(tms) {
      if (!this.dragEvent || this.dragTime === null) return;
      const duration =
        this.dragOrigin.end.getTime() - this.dragOrigin.start.getTime();
      const start = this.roundTime(this.toTime(tms) - this.dragTime);
      // Nao deixa a aula cair num horario que ja passou.
      if (start <= Date.now()) return;
      if (start === this.dragEvent.start.getTime()) return;
      this.dragMoved = true;
      this.dragging = true;
      this.dragEvent.start = new Date(start);
      this.dragEvent.end = new Date(start + duration);
    },
    endDrag() {
      const event = this.dragEvent;
      const moved = this.dragMoved;
      const origin = this.dragOrigin;
      this.dragEvent = null;
      this.dragTime = null;
      this.dragMoved = false;
      this.dragging = false;
      if (!event || !moved) return;

      // Um arrasto de verdade nao deve abrir o cartao da aula ao soltar.
      this.lastDragEnd = Date.now();
      this.selectedOpen = false;
      this.moveRequest = {
        event,
        origin,
        fromLabel: this.formatMoment(origin.start),
        toLabel: this.formatMoment(event.start),
        saving: false,
      };
    },
    // Saiu da grade com o botao apertado: a aula volta para onde estava.
    cancelDrag() {
      if (!this.dragEvent) return;
      if (this.dragMoved) this.restoreEvent(this.dragEvent, this.dragOrigin);
      this.dragEvent = null;
      this.dragTime = null;
      this.dragMoved = false;
      this.dragging = false;
    },
    restoreEvent(event, origin) {
      event.start = new Date(origin.start);
      event.end = new Date(origin.end);
    },
    cancelMove() {
      if (!this.moveRequest || this.moveRequest.saving) return;
      this.restoreEvent(this.moveRequest.event, this.moveRequest.origin);
      this.moveRequest = null;
    },
    confirmMove() {
      const request = this.moveRequest;
      if (!request || request.saving) return;
      request.saving = true;

      const pad = (value) => String(value).padStart(2, "0");
      const start = request.event.start;
      const date = [
        start.getFullYear(),
        pad(start.getMonth() + 1),
        pad(start.getDate()),
      ].join("-");
      const hour = pad(start.getHours()) + ":" + pad(start.getMinutes());

      partnerApi
        .moveEvent(request.event.id, { date, hour })
        .then(() => {
          this.moveRequest = null;
          this.$root.$emit("display-alert", {
            text: this.$t("calendar.moveDone", { when: request.toLabel }),
            type: 0,
          });
          this.$emit("reload");
        })
        .catch((error) => {
          this.restoreEvent(request.event, request.origin);
          this.moveRequest = null;
          this.$root.$emit("display-alert", {
            text:
              error?.response?.data?.message || this.$t("calendar.moveFailed"),
            type: 1,
          });
        });
    },
    formatMoment(value) {
      return new Intl.DateTimeFormat(languageTag(), {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(value);
    },
    showEvent({ nativeEvent, event }) {
      // Soltar depois de arrastar tambem dispara o clique: ignora.
      if (Date.now() - this.lastDragEnd < 300) return;
      const open = () => {
        this.selectedEvent = event;
        this.selectedElement = nativeEvent.target;
        requestAnimationFrame(() =>
          requestAnimationFrame(() => (this.selectedOpen = true))
        );
      };

      if (this.selectedOpen) {
        this.selectedOpen = false;
        requestAnimationFrame(() => requestAnimationFrame(() => open()));
      } else {
        open();
      }

      nativeEvent.stopPropagation();
    },
    openLink(url) {
      window.open(url, "_blank");
    },
    // Registra a entrada do aluno (presença sugerida) sem atrasar nem barrar o Meet.
    joinLesson(item) {
      if (this.module === "aluno/curso" && item.id && !item.isGeneralClass) {
        Promise.resolve(customerApi.joinLesson(item.id)).catch(() => {});
      }
      this.openLink(item.link);
    },
    // A confirmação da conversação vale até o horário de início da aula.
    hasStarted(item) {
      const start = item && this.parseDate(item.start);
      return !!start && start <= new Date();
    },
    confirmGeneralClassParticipation(item) {
      if (!item.generalClassId) return;

      this.selectedOpen = false;
      this.$emit("participate-general-class", item.generalClassId);
    },
    declineGeneralClassParticipation(item) {
      if (!item.generalClassId) return;

      this.selectedOpen = false;
      this.$emit("decline-general-class", item.generalClassId);
    },
    registerGeneralClassAttendance(item) {
      if (!item.generalClassId) return;

      this.selectedOpen = false;
      this.$emit("general-class-attendance", item.generalClassId);
    },
    setupAulas(item) {
      this.focused = item;
      this.$refs.dialog.open();
    },
    confirmClass(item) {
      partnerApi.confirmClass(item.id).then(() => {
        this.$root.$emit("display-alert", {
          text: this.$t("calendar.lessonConfirmed"),
          type: 0,
        });
      });
      this.$refs.dialog.close();
      this.$emit("reload");
    },
    modalAdiar(item) {
      this.$refs.dialog.close();
      this.adiando = item;
      this.$refs.adiar.open();
    },
    modalAdiarAluno(item) {
      this.adiando = item;
      this.$refs.adiarAluno.open();
    },
    adiarAula(data) {
      this.$refs.adiar.close();
      this.$emit("adiar", data);
    },
    adiarAulaAluno(data) {
      this.$refs.adiarAluno.close();
      this.$emit("adiar", data);
    },
    showLevel(level) {
      if (level !== undefined && level !== null) {
        const currentLevel = this.englishLevels.find(
          (item) => item.value === +level
        );
        if (currentLevel) return currentLevel.text;
      }

      return this.$t("common.awaitingPlacement");
    },
    formatDuration(durationInMinutes) {
      if (!durationInMinutes) return this.$t("common.notInformed");

      const hours = Math.floor(durationInMinutes / 60);
      const minutes = durationInMinutes % 60;

      if (hours && minutes)
        return `${hours}h${minutes.toString().padStart(2, "0")}`;
      if (hours) return `${hours}h`;
      return `${minutes}min`;
    },
    formatDateTime(value) {
      if (!value) return "";

      const date = value instanceof Date ? value : this.parseDate(value);

      if (!date || Number.isNaN(date.getTime())) return "";

      return new Intl.DateTimeFormat(languageTag(), {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    },
    parseContentReference(content) {
      const normalized = String(content || "").trim();

      if (!normalized) {
        return {
          moduleId: null,
          materialId: null,
        };
      }

      const [moduleId, materialId] = normalized.split("/");

      return {
        moduleId: moduleId || null,
        materialId: materialId || null,
      };
    },
    buildMaterialUrl(content) {
      const { moduleId, materialId } = this.parseContentReference(content);

      if (!moduleId) return null;

      const params = new URLSearchParams();

      if (materialId) {
        params.set("material", materialId);
      }

      if (this.selectedEvent?.link) {
        params.set("classLink", this.selectedEvent.link);
      }

      if (this.module === "professor/curso") {
        const baseUrl = `${window.location.origin}/connect/professor/curso/material/${moduleId}`;
        const query = params.toString();
        return query ? `${baseUrl}?${query}` : baseUrl;
      }

      const baseUrl = `${window.location.origin}/connect/aluno/curso/${moduleId}`;
      const query = params.toString();
      return query ? `${baseUrl}?${query}` : baseUrl;
    },
    abrirMaterial(item) {
      const materialUrl = this.buildMaterialUrl(item.content);

      if (item.isGeneralClass) {
        if (materialUrl) {
          window.open(materialUrl, "_blank");
          return;
        }

        this.openLink(item.materialLink);
        return;
      }

      if (!materialUrl) return;

      window.open(materialUrl, "_blank");
    },
  },
};
</script>

<style scoped>
.cal {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --paper: var(--dk-paper, #faf7fc);
  --line: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  width: 100%;
  color: var(--ink);
}
.cal__col {
  width: 100%;
}
.cal-display {
  font-family: "Poppins", sans-serif;
  letter-spacing: -0.01em;
}

/* ---------- Control bar ---------- */
.cal__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.cal__nav {
  display: flex;
  align-items: center;
  gap: 8px;
}
.cal__today {
  padding: 7px 16px;
  border-radius: 11px;
  border: 1px solid var(--line);
  background: var(--dk-surface, #fff);
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--ink);
  cursor: pointer;
  transition: background 0.15s ease;
}
.cal__today:hover {
  background: var(--paper);
}
.cal__step {
  width: 36px;
  height: 36px;
  border-radius: 11px;
  border: 1px solid var(--line);
  background: var(--dk-surface, #fff);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;
}
.cal__step:hover {
  background: var(--paper);
}
.cal__today:focus-visible,
.cal__step:focus-visible,
.cal__view:focus-visible {
  outline: 2px solid var(--cal-accent);
  outline-offset: 2px;
}
.cal__title {
  margin-left: 8px;
  font-size: clamp(1.3rem, 2.6vw, 1.75rem);
  font-weight: 700;
  color: var(--ink);
  text-transform: capitalize;
  white-space: nowrap;
}
.cal__views {
  display: inline-flex;
  padding: 4px;
  border-radius: 13px;
  background: var(--paper);
  border: 1px solid var(--line);
  flex-wrap: wrap;
}
.cal__view {
  padding: 7px 14px;
  border-radius: 9px;
  border: none;
  background: transparent;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--muted);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.cal__view:hover {
  color: var(--ink);
}
.cal__view--on {
  background: var(--dk-surface, #fff);
  color: var(--cal-accent);
  box-shadow: 0 6px 16px -10px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.6);
}

/* ---------- Mudar o horario arrastando ---------- */
.cal__hint {
  margin: -6px 0 12px;
  font-size: 0.8rem;
  color: var(--muted);
}
/* Disponibilidade do perfil enquanto a aula é arrastada: fica atrás das aulas
   e não captura o mouse (o arrasto segue lendo o horário sob o ponteiro). */
.cal__grid >>> .v-event-timed-container {
  z-index: 1;
}
.cal-avail {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 0;
  pointer-events: none;
  background: rgba(0, 163, 121, 0.14);
  box-shadow: inset 3px 0 0 rgba(0, 163, 121, 0.55);
}
.cal__grid--dragging {
  user-select: none;
  cursor: grabbing;
}
.cal-evt--movable {
  cursor: grab;
}
.cal-evt--dragging {
  cursor: grabbing;
  filter: drop-shadow(0 6px 10px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.35));
}
.mv {
  padding: 22px 22px 18px;
  border-radius: 16px !important;
  font-family: "Poppins", sans-serif;
  color: var(--dk-ink, #231a2e);
}
.mv__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
}
.mv__name {
  margin: 2px 0 14px;
  font-size: 0.82rem;
  color: var(--dk-muted, #6b6472);
}
.mv__when {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 6px 14px;
  margin: 0 0 14px;
  padding: 12px 14px;
  border-radius: 12px;
  background: var(--cal-accent-soft);
  font-size: 0.88rem;
}
.mv__when dt {
  color: var(--dk-muted, #6b6472);
}
.mv__when dd {
  margin: 0;
  text-transform: capitalize;
}
.mv__old {
  text-decoration: line-through;
  color: var(--dk-muted, #6b6472);
}
.mv__new {
  font-weight: 700;
  color: var(--cal-accent);
}
.mv__note {
  margin: 0 0 16px;
  font-size: 0.78rem;
  line-height: 1.5;
  color: var(--dk-muted, #6b6472);
}
.mv__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.mv__btn {
  padding: 8px 16px;
  border: 1px solid transparent;
  border-radius: 11px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s ease;
}
.mv__btn:disabled {
  opacity: 0.55;
  cursor: default;
}
.mv__btn--ghost {
  background: transparent;
  color: var(--dk-ink, #231a2e);
}
.mv__btn--solid {
  background: var(--cal-accent);
  color: #fff;
}
.mv__btn:focus-visible {
  outline: 2px solid var(--cal-accent);
  outline-offset: 2px;
}

/* ---------- Grade (v-calendar) ---------- */
/* Sem altura explicita o v-calendar encolhe para o minimo e sobra tela vazia
   embaixo. A altura acompanha a janela, descontando cabecalho da pagina e barra
   de controles; o piso evita esmagar as linhas em telas baixas e o teto evita
   linhas gigantes em monitores muito altos. */
.cal__grid {
  height: clamp(560px, calc(100vh - 260px), 1100px);
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 18px 40px -34px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.5);
}
.cal__grid >>> .v-calendar {
  border: none;
  height: 100%;
}
/* Semana/Dia rolam por dentro da grade em vez de esticar a pagina. */
.cal__grid >>> .v-calendar-daily {
  height: 100%;
}
.cal__grid >>> .v-calendar-weekly__head-weekday {
  padding: 10px 0;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
  border-color: var(--line);
}
.cal__grid >>> .v-calendar-weekly__day,
.cal__grid >>> .v-calendar-weekly__head-weekday,
.cal__grid >>> .v-calendar-daily__day,
.cal__grid >>> .v-calendar-daily__intervals-body,
.cal__grid >>> .v-calendar-daily__day-interval,
.cal__grid >>> .v-calendar-daily_head-day,
.cal__grid >>> .v-calendar-daily__intervals-head {
  border-color: var(--line) !important;
}
.cal__grid >>> .v-calendar-weekly__day.v-outside {
  background: var(--paper);
}
.cal__grid >>> .v-calendar-weekly__day-label {
  margin: 6px 0 2px;
}
.cal__grid >>> .v-calendar-weekly__day-label .v-btn {
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--ink);
  background: transparent !important;
  box-shadow: none;
  height: 30px;
  width: 30px;
}
.cal__grid
  >>> .v-calendar-weekly__day.v-present
  .v-calendar-weekly__day-label
  .v-btn,
.cal__grid
  >>> .v-calendar-daily_head-day.v-present
  .v-calendar-daily_head-day-label
  .v-btn {
  background: var(--cal-accent) !important;
  color: #fff !important;
  box-shadow: 0 8px 16px -8px var(--cal-accent);
}
.cal__grid >>> .v-calendar-weekly__day.v-present {
  background: var(--cal-accent-soft);
}
.cal__grid >>> .v-calendar-daily_head-weekday {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}
.cal__grid >>> .v-calendar-daily_head-day-label .v-btn {
  background: transparent !important;
  box-shadow: none;
  color: var(--ink);
  font-weight: 700;
}
.cal__grid >>> .v-calendar-daily__interval-text {
  font-size: 0.7rem;
  color: var(--muted);
}

/* eventos */
.cal__grid >>> .v-event,
.cal__grid >>> .v-event-timed {
  border-radius: 7px !important;
  font-weight: 600;
  font-size: 0.76rem;
  padding: 0 8px;
  border: none !important;
}
.cal__grid >>> .v-event-more {
  font-weight: 700;
  font-size: 0.72rem;
  color: var(--cal-accent) !important;
}

/* conteudo do evento (slot) */
.cal-evt {
  height: 100%;
  display: flex;
  align-items: center;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: #fff;
}
/* aula geral pendente de aceite: sem fundo, borda tracejada na cor do evento */
.cal-evt--tentative {
  color: var(--evt-color);
  border: 1.5px dashed var(--evt-color);
  border-radius: 7px;
  background: transparent;
  padding: 0 6px;
  font-weight: 700;
}

/* ---------- Cartão-ingresso (popover) ---------- */
.tkt {
  border-radius: 18px !important;
  overflow: hidden;
  border: 1px solid rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09) !important;
  box-shadow: 0 30px 60px -30px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.55) !important;
  color: var(--dk-ink, #231a2e);
}
.tkt__accent {
  height: 5px;
  width: 100%;
}
.tkt__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 16px 18px 10px;
}
.tkt__head-main {
  min-width: 0;
}
.tkt__name {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 1.05rem;
  font-weight: 800;
  color: var(--dk-ink, #231a2e);
  line-height: 1.25;
}
.tkt__time {
  margin-top: 3px;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--cal-accent);
  font-variant-numeric: tabular-nums;
}
.tkt__more {
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  background: var(--dk-surface, #fff);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.tkt__body {
  padding: 4px 18px 8px;
}
.tkt__desc {
  margin: 0 0 12px;
  font-size: 0.88rem;
  color: var(--dk-ink-soft, #4b4551);
  line-height: 1.5;
}
.tkt__facts {
  display: grid;
  gap: 8px;
}
.tkt__fact {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}
.tkt__fact-k {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--dk-muted, #6b6472);
}
.tkt__fact-v {
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--dk-ink, #231a2e);
  text-align: right;
}
.tkt__att--present {
  color: var(--dk-teal, #00a379);
}
.tkt__att--absent {
  color: var(--dk-red, #d52323);
}
.tkt__note {
  margin: 12px 0 0;
  font-size: 0.82rem;
  color: var(--dk-muted, #6b6472);
}
.tkt__students {
  margin-top: 12px;
}
.tkt__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.tkt__chip {
  padding: 3px 10px;
  border-radius: 999px;
  background: var(--dk-paper, #faf7fc);
  border: 1px solid rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  font-size: 0.76rem;
  font-weight: 600;
  color: var(--dk-ink, #231a2e);
}
.tkt__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 18px 16px;
  border-top: 1px solid rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  margin-top: 8px;
}
.tkt__spacer {
  flex: 1 1 auto;
}
.tkt__btn {
  padding: 8px 14px;
  border-radius: 11px;
  font-size: 0.83rem;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;
  transition: filter 0.15s ease, background 0.15s ease;
}
.tkt__btn--ghost {
  background: var(--dk-surface, #fff);
  border-color: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.12);
  color: var(--dk-ink, #231a2e);
}
.tkt__btn--ghost:hover {
  background: var(--dk-paper, #faf7fc);
}
.tkt__btn--solid {
  background: var(--cal-accent);
  color: #fff;
}
.tkt__btn--solid:hover {
  filter: brightness(1.06);
}
.tkt__btn--danger {
  background: var(--dk-surface, #fff);
  border-color: rgba(213, 35, 35, 0.4);
  color: var(--dk-red, #a61b1b);
}
.tkt__btn--danger:hover {
  background: rgba(213, 35, 35, 0.06);
}
.tkt__btn:focus-visible {
  outline: 2px solid var(--cal-accent);
  outline-offset: 2px;
}
.tkt__confirmed {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--dk-teal-deep, #007f5e);
}

@media (max-width: 600px) {
  .cal__bar {
    gap: 12px;
  }
  .cal__title {
    margin-left: 0;
    order: -1;
    width: 100%;
  }
  /* No celular a barra de controles quebra em duas linhas e o cabecalho ocupa
     mais espaco, entao sobra menos altura para a grade. */
  .cal__grid {
    height: clamp(460px, calc(100vh - 320px), 900px);
  }
}
</style>
